import React, {useEffect, useState} from "react";
import {useRouter} from "next/router";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  CheckoutItem,
  clearError,
  clearUpdateAlert,
  createSalesOrder,
  fetchCheckoutData,
  removePromoCode,
  reset,
  selectButtonDestination,
  selectButtonTitle,
  selectError,
  selectIsLoading,
  selectIsPurchasing,
  selectIsUpdating,
  selectPurchaseError,
  selectPurchaseOrder,
  selectSalesOrder,
  selectSections,
  selectShowCartUpdateAlert
} from "../../redux/checkout/checkoutSlice";
import {AlertDialog, AlertDialogButtonProps} from "../dialogs/AlertDialog";
import {Container} from "../containers/Container";
import {Heading2, Heading3, HeroHeading2, LocalizedTextLabel, Paragraph} from "../text";
import Skeleton from "react-loading-skeleton";
import {CheckoutItemView} from "./CheckoutItemView";
import {CheckoutOptionItemView} from "./CheckoutOptionItemView";
import {Button} from "../buttons/Button";
import {CheckoutPaymentSummaryView} from "./CheckoutPaymentSummaryView";
import {NavBarPadding} from "../navBars/NavBarMain";
import {useModalRouting} from "../../utils/useModalRouting";
import {CurrencyUtils} from "../../utils/currencyUtils";
import {ApiError} from "../../errors/apiError";
import {selectUser} from "../../redux/user/userSlice";
import {RealmServiceError} from "../../errors/realmServiceError";
import {EventLogger} from "../../utils/eventLogger";
import {AnalyticsEvent} from "../../utils/eventProperties";

export interface CheckoutViewProps {
  storeId?: string
}

export const CheckoutView: React.FC<CheckoutViewProps> = ({ storeId }) => {
  const router = useRouter()
  const { t } = useTranslation()
  const { createRoute  } = useModalRouting()
  const dispatch = useAppDispatch()

  const user = useAppSelector(selectUser)
  const sections = useAppSelector(selectSections)
  const purchaseOrder = useAppSelector(selectPurchaseOrder)
  const salesOrder = useAppSelector(selectSalesOrder)
  const purchaseError = useAppSelector(selectPurchaseError)
  const error = useAppSelector(selectError)
  const loading = useAppSelector(selectIsLoading)
  const isPurchasing = useAppSelector(selectIsPurchasing)
  const isUpdating = useAppSelector(selectIsUpdating)
  const showCartUpdateAlert = useAppSelector(selectShowCartUpdateAlert)

  const buttonTitle = useAppSelector(selectButtonTitle)
  const buttonDestination = useAppSelector(selectButtonDestination)

  const [isLoading, setIsLoading] = useState(loading)
  const [errorButtons, setErrorButtons] = useState<AlertDialogButtonProps[]>([])

  useEffect(() => {
    EventLogger.logEvent(new AnalyticsEvent({ eventName: 'Screen Visited', screenName: 'Checkout' }))
  }, [])

  useEffect(() => {
    dispatch(reset())

    if (storeId) {
      dispatch(fetchCheckoutData({ storeId }))
    }

    return () => {
      dispatch(reset())
    }
  }, [storeId, dispatch])

  useEffect(() => {
    if (salesOrder?.id) {
      if (user?.roles.includes('anonymous')) {
        router.push(
          createRoute({
            rootPath: '/orders',
            name: 'login',
            query: { config: 'post_purchase' },
            returnTo: { name: 'order_detail', query: { order_id: salesOrder.id } }
          }),
          { pathname: 'order-detail', query: { order_id: salesOrder.id } },
          { shallow: true }
        ).then(() => {
          dispatch(reset())
        })
      } else {
        router.push(
          createRoute({ rootPath: '/orders', name: 'order_detail', query: { order_id: salesOrder.id } }),
          { pathname: '/order-detail', query: { order_id: salesOrder.id } },
          { shallow: true }
        ).then(() => {
          dispatch(reset())
        })
      }
    }
  }, [salesOrder])

  useEffect(() => {
    if (!storeId) {
      setIsLoading(true)
    } else {
      setIsLoading(loading)
    }
  }, [storeId, loading])

  useEffect(() => {
    if (error instanceof ApiError && error.code === 'INVALID_PROMO_CODE') {
      setErrorButtons([
        {
          title: { key: 'common:common_title_ok' },
          onClick: () => {
            dispatch(removePromoCode())
          },
          style: 'default'
        }
      ])
    } else if (error instanceof ApiError && error.code === 'UNAUTHORIZED' || error instanceof RealmServiceError && error.code === 'NOT_LOGGED_IN') {
      setErrorButtons([
        {
          title: { key: 'common:common_title_ok' },
          onClick: () => {
            router.push(
              createRoute({ name: 'login', query: { config: 'sign_in' }, reloadOnClose: true }),
              undefined,
              { shallow: true }
            );
          },
          style: 'default'
        }
      ])
    } else if (error instanceof ApiError && error.code === 'INVALID_DELIVERY_LOCATION') {
      setErrorButtons([
        {
          title: { key: 'checkout:checkout_button_title_go_back' },
          onClick: () => {
            dispatch(clearError())
            router.back()
          },
          style: 'cancel'
        },
        {
          title: { key: 'checkout:checkout_button_title_change_address' },
          onClick: () => {
            router.push(
              createRoute({ name: 'location_entry' }),
              undefined,
              { shallow: true }
            );
          },
          style: 'default'
        }
      ])
    } else {
      setErrorButtons([
        {
          title: { key: 'checkout:checkout_button_title_go_back' },
          onClick: () => {
            dispatch(clearError())
            router.back()
          },
          style: 'default'
        }
      ])
    }
  }, [error])

  const onButtonClick = () => {
    switch (buttonDestination) {
      case "contact_info_entry":
        router.push(
          createRoute({ name: 'contact_info_entry' }),
          undefined,
          { shallow: true }
        );
        break;
      case "location_entry":
        router.push(
          createRoute({ name: 'location_entry' }),
          undefined,
          { shallow: true }
        );
        break;
      case "payment_entry":
        router.push(
          createRoute({ name: 'payment_entry' }),
          undefined,
          { shallow: true }
        );
        break;
      case "pickup_location_selector":
        break;
      default:
        dispatch(createSalesOrder())
        break;
    }
  }

  return <div className={`w-full max-h-screen sticky lg:absolute lg:h-screen lg:top-0`}>
    <div className={`hidden lg:block ${NavBarPadding}`} />
    <div className={`flex flex-row w-full h-full`}>
      <AlertDialog
        isOpen={!!error}
        error={error}
        isDismissable={false}
        buttons={errorButtons}
      />
      <AlertDialog
        isOpen={showCartUpdateAlert}
        title={{ key: 'checkout:checkout_alert_title_removed_items' }}
        message={{ key: 'checkout:checkout_alert_message_removed_items' }}
        buttons={[
          {
            title: { key: 'common:common_title_ok' },
            onClick: () => {
              dispatch(clearUpdateAlert())
            },
            style: 'default'
          }
        ]}
      />
      <AlertDialog
        isOpen={!!purchaseError}
        error={purchaseError}
        isDismissable={false}
        buttons={[
          {
            title: { key: 'common:common_title_retry' },
            onClick: () => {
              dispatch(clearError())
              dispatch(createSalesOrder())
            },
            style: 'default'
          },
          {
            title: { key: 'common:common_title_ok' },
            onClick: () => {
              dispatch(clearError())
            },
            style: 'cancel'
          }
        ]}
      />

      <div className={`flex-grow xl:col-span-3 w-full h-full flex flex-col overflow-hidden`}>
        <div className={`flex-1 overflow-y-scroll`}>
          <Container className="w-full max-w-4xl h-full">
            <HeroHeading2 className="mb-8">{isLoading ? <Skeleton className="!w-2/5" /> : purchaseOrder?.vendor?.name}</HeroHeading2>

            {sections.map((section, index) => {
              return <div key={section.id}>
                <Heading2 className="mb-2 mt-8">{isLoading ? <Skeleton className="!w-1/3" /> : <LocalizedTextLabel t={t} text={section.title} />}</Heading2>

                {section.items.map(item => {
                  if (item instanceof CheckoutItem) {
                    return <CheckoutItemView key={item.id} item={item} isLoading={isLoading} />
                  } else {
                    return <CheckoutOptionItemView key={item.id} item={item} isLoading={isLoading} />
                  }
                })}
              </div>
            })}

            <div className="lg:hidden pb-12">
              <Heading2 className="mb-7 mt-8">{isLoading ? <Skeleton className="!w-1/3" /> : t('checkout:checkout_section_header_title_summary')}</Heading2>

              <div className="flex">
                <Paragraph className="text-left">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : t('checkout:checkout_row_title_subtotal')}</Paragraph>
                <Paragraph className="text-right flex-grow">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : CurrencyUtils.stringFromPrice(purchaseOrder?.paymentSummary?.subtotal, purchaseOrder?.paymentSummary?.currencyCode)}</Paragraph>
              </div>

              <div className="flex mt-4">
                <Paragraph className="text-left">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : t('checkout:checkout_row_title_shipping')}</Paragraph>
                <Paragraph className="text-right flex-grow">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : CurrencyUtils.stringFromPrice(purchaseOrder?.paymentSummary?.shipping, purchaseOrder?.paymentSummary?.currencyCode)}</Paragraph>
              </div>

              <div className={`flex mt-4 ${purchaseOrder?.paymentSummary?.discount ? '' : 'hidden'}`}>
                <Paragraph className="text-left text-fg-primary">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : t('checkout:checkout_row_title_discount')}</Paragraph>
                <Paragraph className="text-right text-fg-primary flex-grow">-{isLoading || isUpdating ? <Skeleton className="!w-16" /> : CurrencyUtils.stringFromPrice(purchaseOrder?.paymentSummary?.discount, purchaseOrder?.paymentSummary?.currencyCode)}</Paragraph>
              </div>

              <div className="flex mt-4">
                <Paragraph className="text-left">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : t('checkout:checkout_row_title_tax')}</Paragraph>
                <Paragraph className="text-right flex-grow">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : CurrencyUtils.stringFromPrice(purchaseOrder?.paymentSummary?.tax, purchaseOrder?.paymentSummary?.currencyCode)}</Paragraph>
              </div>

              <div className="flex mt-4">
                <Heading3 className="text-left">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : t('checkout:checkout_row_title_total')}</Heading3>
                <Heading3 className="text-right flex-grow">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : CurrencyUtils.stringFromPrice(purchaseOrder?.paymentSummary?.getTotal(), purchaseOrder?.paymentSummary?.currencyCode)}</Heading3>
              </div>
            </div>

            <div className={`my-8 hidden lg:block`}>
              <Button
                isFullWidth={true}
                isLoading={isPurchasing || isUpdating}
                isSkeletonLoading={isLoading}
                onClick={onButtonClick}
                buttonStyle={buttonDestination ? 'secondary' : 'primary'}
                size="lg"
              >
                <LocalizedTextLabel t={t} text={buttonTitle}/>
              </Button>
            </div>

            <div className={`h-36`} />
          </Container>
        </div>
      </div>

      <div className={`flex-shrink-0 w-md xl:col-span-2 w-full hidden lg:block h-full`}>
        <CheckoutPaymentSummaryView onButtonClick={onButtonClick} isLoading={isLoading} />
      </div>
    </div>

    <div className={`fixed z-20 lg:hidden left-0 bottom-0 w-full rounded-full ${isLoading ? 'hidden' : ''}`}>
      <div className="mx-4 my-4 lg:mx-6 lg:mx-6 rounded-full shadow-lg lg:shadow-none">
        <Button
          isFullWidth={true}
          isLoading={isPurchasing || isUpdating}
          isSkeletonLoading={isLoading}
          onClick={onButtonClick}
          buttonStyle={buttonDestination ? 'secondary' : 'primary'}
          size="lg"
        >
          {
            (() => {
              const total = purchaseOrder?.paymentSummary?.getTotal()
              const totalText = total ? ' – ' + CurrencyUtils.stringFromPrice(total, purchaseOrder?.paymentSummary?.currencyCode) : ''
              return <span>
                <LocalizedTextLabel t={t} text={buttonTitle}/> {totalText}
              </span>
            })()
          }
        </Button>
      </div>
    </div>
  </div>
}